<template>
  <div
    v-if="!subscriptionIsFreeTrial && $store.state.idea.storyMode.common.completedSteps.employeesAndPayrollCompleted === true && isStaffPayrollVisible"
    class="journal-content dynamic-height"
    :class="{'dynamic-height': !isOption(staffAndPayrollData, 'breakdownSection') && !isOption(staffAndPayrollData, 'timelineSection') && !isOption(staffAndPayrollData, 'tableSection')}"
  >
    <div class="content-menus">
      <div v-if="moreVisible === true" class="view-type">
        <template v-if="employeesAndPayroll.length !== 0">
          <a
            class="orange"
            :class="{'orange-off': !isOption(staffAndPayrollData, 'breakdownSection')}"
            @click="enableBreakdown"
          >
            {{ $t('pages.businessPlan.sections.breakdown') }}
          </a>
          <a
            class="orange"
            :class="{'orange-off': !isOption(staffAndPayrollData, 'timelineSection')}"
            @click="enableTimeline"
          >
            {{ $t('pages.businessPlan.sections.timeline') }}
          </a>
          <a
            class="orange"
            :class="{'orange-off': !isOption(staffAndPayrollData, 'tableSection')}"
            @click="enableTable"
          >
            {{ $t('pages.businessPlan.sections.table') }}
          </a>
        </template>
        <p v-if="employeesAndPayroll.length === 0">
          {{ $t('pages.businessPlan.sections.youDontHaveAnyDataOnThisPage') }}
        </p>
      </div>
      <div v-if="canEdit" class="mini-actions">
        <toggle-page-items-button
          :visible="moreVisible"
          @click="toggleMore"
        />
        <toggle-view-button
          :disabled="!cardExist"
          :horizontal="!isOption(staffAndPayrollData, 'gridListView')"
          @click="toggleRotate"
        />
        <toggle-notes-button
          :disabled="!moreDetails"
          :visible="isOption(staffAndPayrollData, 'moreDetailsDisplay')"
          @click="enableMoreDetails"
        />
        <edit-section-link route-name="business-guide-set-up-staff-and-payroll" />
      </div>
    </div>

    <div class="sub chapter">
      <header>
        <!-- Title section -->
        <editable-title
          v-model="staffAndPayrollData.title"
          tag="h1"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Title section -->
        <hr>
        <!-- Description section -->
        <editable-description
          v-model="staffAndPayrollData.subTitle"
          :empty-text="$t('addMoreDetails.addNoteText')"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Description section -->
      </header>

      <template v-if="employeesAndPayroll.length !== 0">
        <!-- Breakdown chart -->
        <div v-if="isOption(staffAndPayrollData, 'breakdownSection')" class="switcher-inner-wrapper breakdown-chart">
          <staff-and-payroll-breakdown
            :chart-adjustment="chartAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Breakdown chart -->

        <!-- Timeline chart -->
        <div v-if="isOption(staffAndPayrollData, 'timelineSection')" class="timeline-chart">
          <staff-and-payroll-timeline
            :switch-selector="switchSelector"
            :chart-adjustment="chartAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Timeline chart -->

        <!-- Finance Table -->
        <div v-if="isOption(staffAndPayrollData, 'tableSection')" class="table-chart">
          <staff-and-payroll-table
            :table-adjustment="tableAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Finance Table -->
      </template>

      <!-- More Details section -->
      <template v-if="moreDetails && isOption(staffAndPayrollData, 'moreDetailsDisplay')">
        <div class="details">
          <editable-title
            v-model="staffAndPayrollData.moreDetailsTitle"
            class="bold"
            tag="p"
            :disabled="!canEdit"
            @save="onSave"
          />
          <p v-html="moreDetails" />
        </div>
      </template>
      <!-- More Details section -->
    </div>
  </div>
</template>

<script>
import EditSectionLink from '@/views/Home/StoryMode/Components/BusinessPlan/EditSectionLink'
import EditableDescription from '@/components/_v2/EditableDescription'
import EditableTitle from '@/components/_v2/EditableTitle'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import MixinUserSubscription from '@/mixins/permissions/userSubscription'
import StaffAndPayrollBreakdown from '@/views/Home/StoryMode/SetUp/StaffAndPayroll/Charts/StaffAndPayrollBreakdown'
import StaffAndPayrollTable from '@/views/Home/StoryMode/SetUp/StaffAndPayroll/Tables/StaffAndPayrollTable'
import StaffAndPayrollTimeline from '@/views/Home/StoryMode/SetUp/StaffAndPayroll/Charts/StaffAndPayrollTimeline'
import ToggleNotesButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleNotesButton'
import TogglePageItemsButton from '@/views/Home/StoryMode/Components/BusinessPlan/TogglePageItemsButton'
import ToggleViewButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleViewButton'
import journalMethods from '@/mixins/journalMethods'

export default {
  name: 'StaffAndPayroll',

  components: {
    ToggleNotesButton,
    TogglePageItemsButton,
    ToggleViewButton,
    EditSectionLink,
    StaffAndPayrollBreakdown,
    StaffAndPayrollTimeline,
    StaffAndPayrollTable,
    EditableTitle,
    EditableDescription
  },

  mixins: [
    journalMethods,
    MixinIdeaRoles,
    MixinUserSubscription
  ],

  props: {
    allPagesTextData: {
      type: Object,
      default: () => ({})
    },
    shareMode: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      cardExist: false,
      staffAndPayrollData: {},

      moreVisible: false,
      updateCharts: true,

      moreDetails: this.$store.state.idea.storyMode.common.moreDetails.employeesAndPayrollMoreDetails,
      employeesAndPayroll: this.$store.state.idea.storyMode.setUp.staffAndPayrolls.staffAndPayrolls,

      chartAdjustment: {
        chartHeight: 260,
        headingTitleFontSize: '18px',
        xLabelTitleFontSize: '10px',
        yLabelTitleFontSize: '12px',
        yaxisLabelsMinWidth: {
          breakDown: 0,
          timeLine: 0
        }
      },
      tableAdjustment: {
        journalPage: true,
        period: 'yearly',
        tableFirstColumnWidth: 250,
        columnWidth: 118
      },
      switchSelector: 'payroll'
    }
  },

  computed: {
    isStaffPayrollVisible () {
      const index = this.$store.state.idea.journal.journalSwitchBars.indexOf(
        this.$store.state.idea.journal.journalSwitchBars.find(journalElement => journalElement.sectionName === 'StaffAndPayroll')
      )

      return !this.$store.state.idea.journal.journalSwitchBars[index].options.includes('switch')
    },

    isTitleEnabled () {
      return this.staffAndPayrollData.title
    },

    isMoreDetailsTitleEnabled () {
      return this.staffAndPayrollData.moreDetailsTitle
    }
  },

  watch: {
    allPagesTextData () {
      this.staffAndPayrollData = Object.assign({}, this.allPagesTextData)
    }
  },

  mounted () {
    this.staffAndPayrollData = Object.assign({}, this.allPagesTextData)
  },

  methods: {
    toggleRotate () {
      if (this.cardExist) {
        this.toggleOption(this.staffAndPayrollData, 'gridListView')
        this.staffAndPayrollData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    enableMoreDetails () {
      if (this.moreDetails) {
        this.toggleOption(this.staffAndPayrollData, 'moreDetailsDisplay')
        this.staffAndPayrollData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    onSave () {
      this.$emit('save', this.staffAndPayrollData)
    },

    enableTimeline () {
      this.toggleOption(this.staffAndPayrollData, 'timelineSection')
      this.staffAndPayrollData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableBreakdown () {
      this.toggleOption(this.staffAndPayrollData, 'breakdownSection')
      this.staffAndPayrollData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableTable () {
      this.toggleOption(this.staffAndPayrollData, 'tableSection')
      this.staffAndPayrollData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    }
  }
}
</script>
